import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaUndo } from "react-icons/fa";

import Button from "../../../../components/Utils/Button/Button";
import ButtonSwitcher, {
  ButtonSwitcherValue,
} from "../../../../components/Utils/ButtonSwitcher/ButtonSwitcher";
import StyledDropdown from "../../../../components/Utils/StyledDropdown/StyledDropdown";
import {
  StyledInputDate,
  StyledInputNumber,
  StyledInputText,
} from "../../../../components/Utils/StyledInput/StyledInput";
import StyledInputMultiFile from "../../../../components/Utils/StyledInputMultiFile/StyledInputMultiFile";
import useAccountingStore, {
  createInitialExpense,
  getDefaultAccountingTvaRate,
} from "../../../../stores/AccountingStore";
import useLoadingStore from "../../../../stores/LoadingStore";
import useUserStore from "../../../../stores/UserStore";
import AccountingExpenseType from "../../../../types/AccountingExpense";
import EventType from "../../../../types/Event";
import User from "../../../../types/User";
import { formatDateParam } from "../../../../Utils/formatDateParam";
import appFetch from "../../../../Utils/Services/Fetch/appFetch";

interface AccountingFormProps {
  expenseToEdit: AccountingExpenseType | null;
  expenseType: ButtonSwitcherValue;
  partyList: EventType[];
}

// Record VAT either as a rate or as an absolute amount (for receipts mixing
// several VAT rates).
const TVA_MODES: ButtonSwitcherValue[] = [
  { id: "rate", name: "Taux (%)" },
  { id: "amount", name: "Montant (€)" },
];

const Expense: React.FC<AccountingFormProps> = ({
  expenseToEdit,
  expenseType,
  partyList,
}) => {
  const [usersList, setUsersList] = useState<User[]>([]);
  const [tvaMode, setTvaMode] = useState<ButtonSwitcherValue>(TVA_MODES[0]);

  const { loadingStates, setLoading } = useLoadingStore();
  const { expense, setExpense } = useAccountingStore();
  const { isVatSubject } = useUserStore();

  const handleTvaModeChange = (mode: ButtonSwitcherValue) => {
    setTvaMode(mode);
    setExpense((prevExpense) => ({
      ...prevExpense,
      tva_rate:
        mode.id === "rate"
          ? (prevExpense.tva_rate ?? getDefaultAccountingTvaRate())
          : null,
      tva_amount: mode.id === "amount" ? (prevExpense.tva_amount ?? 0) : null,
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      const adminsData = await appFetch("/api/users/list");
      if (adminsData && !adminsData.unauthorized) {
        setUsersList([...adminsData, { name: "Organization", user_id: -1 }]);
      }
    };
    fetchData();
  }, []);

  const setBuyDate = (value: Date) => {
    setExpense((prevExpense) => {
      return { ...prevExpense, recorded_date: value };
    });
  };

  const createExpense = async (type: string) => {
    if (
      !expense.product_name ||
      !expense.price ||
      !expense.recorded_date ||
      !expense.payment_method ||
      !expense.user ||
      (!expense.file && (!expense.files || expense.files.length === 0))
    ) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }
    setLoading("createExpense", true);

    const formData = new FormData();
    formData.append("product_name", expense.product_name);
    formData.append("price", expense.price.toString());
    // Send a calendar-day string (YYYY-MM-DD): recorded_date is a `date`
    // column, so serializing the picked day as a UTC instant would drift it.
    formData.append(
      "recorded_date",
      formatDateParam(expense.recorded_date) ?? "",
    );
    if (isVatSubject) {
      if (tvaMode.id === "amount") {
        formData.append("tva_amount", String(expense.tva_amount ?? 0));
      } else {
        formData.append("tva_rate", String(expense.tva_rate ?? 0));
      }
    }
    formData.append("payment_method", expense.payment_method);
    formData.append("user_id", expense.user.user_id.toString());
    formData.append("expense_id", expense.expense_id.toString());
    if (expense.event?.event_id) {
      formData.append("event_id", expense.event.event_id.toString());
    }

    if (expense.files && expense.files.length > 0) {
      expense.files.forEach((file) => {
        formData.append("files", file);
      });
    } else if (expense.file) {
      formData.append("file", expense.file);
    }

    try {
      const method = type === "addExpense" ? "POST" : "PUT";
      const response = await appFetch(`/api/accounting/${type}`, {
        method,
        body: formData,
      });
      if (response) {
        handleReset();
        toast.success(response.message);
      }
    } catch (error) {
      console.error("Erreur lors de la création de la dépense :", error);
      setLoading("createExpense", false);
    }
    setLoading("createExpense", false);
  };

  useEffect(() => {
    const fetchQuoteDetails = async () => {
      if (!expenseToEdit) {
        return;
      }

      try {
        const expenseDetailData = await appFetch(
          `/api/accounting/getExpenseDetails?expense_id=${expenseToEdit.expense_id}`,
        );

        if (expenseDetailData && expenseDetailData.expense) {
          let file = null;

          if (expenseDetailData.expense.expense.file_path) {
            const fileUrl = `/api/accounting/getExpensesFile?expense_id=${expenseDetailData.expense.expense.expense_id}`;
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            file = new File(
              [blob],
              expenseDetailData.expense.expense.file_path,
              { type: blob.type },
            );
          }

          const loadedExpense = expenseDetailData.expense.expense;
          const isAmountMode =
            loadedExpense.tva_amount !== null &&
            loadedExpense.tva_amount !== undefined;
          setTvaMode(isAmountMode ? TVA_MODES[1] : TVA_MODES[0]);

          setExpense((prevExpense) => ({
            ...prevExpense,
            ...loadedExpense,
            recorded_date: new Date(loadedExpense.recorded_date),
            product_name: loadedExpense.product_name,
            price: loadedExpense.price,
            tva_rate: isAmountMode ? null : (loadedExpense.tva_rate ?? 0),
            tva_amount: isAmountMode ? Number(loadedExpense.tva_amount) : null,
            user:
              expenseDetailData.expense.user.name === null
                ? {
                    name: "Organization",
                    user_id: -1,
                  }
                : expenseDetailData.expense.user,
            event: expenseDetailData.expense.event,
            payment_method: expenseDetailData.expense.expense.payment_method,
            file,
          }));
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des détails de la dépense :",
          error,
        );
      }
    };

    fetchQuoteDetails();
  }, [expenseToEdit, setExpense]);

  useEffect(() => {
    if (expenseToEdit || tvaMode.id !== "rate") {
      return;
    }

    const defaultTvaRate = getDefaultAccountingTvaRate();
    setExpense((prevExpense) => {
      const isPristine =
        prevExpense.expense_id === -1 &&
        !prevExpense.product_name &&
        !prevExpense.price &&
        !prevExpense.payment_method &&
        !prevExpense.event &&
        !prevExpense.file &&
        (!prevExpense.files || prevExpense.files.length === 0);

      if (!isPristine || prevExpense.tva_rate === defaultTvaRate) {
        return prevExpense;
      }

      return {
        ...prevExpense,
        tva_rate: defaultTvaRate,
      };
    });
  }, [expenseToEdit, isVatSubject, setExpense, tvaMode.id]);

  const handleReset = () => {
    setTvaMode(TVA_MODES[0]);
    setExpense(createInitialExpense());
  };

  return (
    <>
      <div className="accountingSection">
        <StyledInputText
          label="Nom*"
          value={expense.product_name}
          setValue={(value: string) => {
            setExpense((prevExpense) => {
              return { ...prevExpense, product_name: value };
            });
          }}
        />
        <StyledInputNumber
          label="Prix TTC*"
          value={expense.price.toString()}
          setValue={(value: string | null) => {
            setExpense((prevExpense) => {
              return { ...prevExpense, price: Number(value) };
            });
          }}
        />
        {isVatSubject && (
          <div className="accountingTvaField">
            <ButtonSwitcher
              value={tvaMode}
              setValue={handleTvaModeChange}
              datas={TVA_MODES}
            />
            {tvaMode.id === "amount" ? (
              <StyledInputNumber
                label="Montant TVA (€)"
                value={(expense.tva_amount ?? 0).toString()}
                setValue={(value: string | null) => {
                  setExpense((prevExpense) => {
                    return {
                      ...prevExpense,
                      tva_amount: Number(value ?? 0),
                      tva_rate: null,
                    };
                  });
                }}
              />
            ) : (
              <StyledInputNumber
                label="TVA (%)"
                value={(expense.tva_rate ?? 0).toString()}
                setValue={(value: string | null) => {
                  setExpense((prevExpense) => {
                    return {
                      ...prevExpense,
                      tva_rate: Number(value ?? 0),
                      tva_amount: null,
                    };
                  });
                }}
              />
            )}
          </div>
        )}
        <StyledDropdown
          label="Payé par*"
          value={expense.user}
          setValue={(value: User | undefined) => {
            setExpense((prevExpense) => {
              return { ...prevExpense, user: value };
            });
          }}
          itemList={usersList}
          itemLabelKey="name"
          itemIdKey="user_id"
        />
        <StyledInputDate
          label="Date*"
          value={expense.recorded_date}
          setValue={setBuyDate}
        />
        <StyledDropdown
          label="Événement"
          value={expense.event}
          setValue={(value: EventType | undefined) => {
            setExpense((prevExpense) => {
              return { ...prevExpense, event: value };
            });
          }}
          itemList={partyList}
          itemLabelKey="name"
          itemIdKey="event_id"
        />
        <StyledDropdown
          label="Moyen de paiement*"
          value={{ payment_method: expense.payment_method }}
          setValue={(value: { payment_method: string } | undefined) => {
            setExpense((prevExpense) => {
              return {
                ...prevExpense,
                payment_method: value?.payment_method ?? "",
              };
            });
          }}
          itemList={[
            { payment_method: "Cash" },
            { payment_method: "Carte bancaire" },
            { payment_method: "Virement" },
          ]}
          itemLabelKey="payment_method"
          itemIdKey="payment_method"
          placeholder="Search..."
        />
        <StyledInputMultiFile
          label="Justificatifs*"
          setValue={(files: File[]) => {
            setExpense((prevExpense) => {
              return { ...prevExpense, files };
            });
          }}
          value={expense.files || []}
          className="accountingFileInput"
          maxFiles={30}
          accept="image/*,application/pdf,.doc,.docx,.txt"
        />
      </div>
      <div className="accountingSection"></div>
      {!expenseToEdit && (
        <>
          <Button
            onClick={() => {
              createExpense("addExpense");
            }}
            className="submitBtn"
            label={`Ajouter la ${expenseType.id}`}
            isLoading={loadingStates.createExpense}
          />
          <Button
            onClick={() => {
              handleReset();
            }}
            className="squaredButton"
            icon={<FaUndo />}
          />
        </>
      )}
      {expenseToEdit && (
        <Button
          onClick={() => {
            createExpense("updateExpense");
          }}
          className="submitBtn"
          label={`Modifider la ${expenseType.id}`}
          isLoading={loadingStates.createExpense}
        />
      )}
    </>
  );
};

export default Expense;
